import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { Message } from 'primereact/message';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { useRef } from 'react';
import { useCart } from '../context/CartContext';
import { useClienteAuth } from '../context/ClienteAuthContext';
import { EcommerceVentaService } from '../services/EcommerceVentaService';
import { EcommerceProductoService } from '../services/EcommerceProductoService';

const Checkout = () => {
    const navigate = useNavigate();
    const toast = useRef(null);
    const { cartItems, getTotal, clearCart } = useCart();
    const { cliente, user } = useClienteAuth();
    const [loading, setLoading] = useState(false);
    const [step, setStep] = useState(1); // 1: Review, 2: Payment, 3: Confirmation
    const [paymentType, setPaymentType] = useState('efectivo');
    const [cotizacionData, setCotizacionData] = useState(null);
    const [error, setError] = useState(null);
    const idEmpresa = 1;
    const idSucursal = 1;

    const totalItems = useMemo(
        () => cartItems.reduce((acc, item) => acc + item.quantity, 0),
        [cartItems]
    );

    const hasOutOfStockItems = useMemo(
        () => cartItems.some((item) => item.quantity > (item.stock || 0)),
        [cartItems]
    );

    // Generar cotización
    const handleGenerateCotizacion = async () => {
        setLoading(true);
        setError(null);
        try {
            const items = cartItems.map(item => ({
                idVariante: item.idVariante || item.id,
                cantidad: item.quantity
            }));

            const cotizacion = await EcommerceProductoService.cotizar(idEmpresa, items);
            setCotizacionData(cotizacion);
            setStep(2);
        } catch (err) {
            setError(err.response?.data?.message || 'Error al generar cotización');
            toast.current?.show({ 
                severity: 'error', 
                summary: 'Error', 
                detail: error,
                life: 5000 
            });
        } finally {
            setLoading(false);
        }
    };

    // Crear venta
    const handleCreateOrder = async () => {
        setLoading(true);
        setError(null);
        try {
            if (!cotizacionData) {
                throw new Error('Cotización no disponible');
            }

            const ventaData = {
                idEmpresa,
                idSucursal,
                idUsuario: user?.id || cliente?.id,
                idCliente: cliente?.id,
                estado: 'completada',
                tipoDePago: paymentType,
                tipoComprobante: 'boleta',
                pagoCon: cotizacionData.total,
                vuelto: 0,
                efectivo: paymentType === 'efectivo' || paymentType === 'mixto' ? cotizacionData.total : 0,
                tarjeta: paymentType === 'tarjeta' || paymentType === 'mixto' ? cotizacionData.total : 0,
                credito: paymentType === 'credito' ? cotizacionData.total : 0,
                montoTotal: cotizacionData.total,
                subTotal: cotizacionData.subTotal,
                detalles: cartItems.map(item => ({
                    idVariante: item.idVariante || item.id,
                    cantidad: item.quantity,
                    precioUnitario: item.price || item.precioVenta,
                    subTotal: (item.price || item.precioVenta) * item.quantity
                }))
            };

            const venta = await EcommerceVentaService.crearVentaCompleta(ventaData);
            
            // Limpiar carrito
            clearCart();
            setCotizacionData(null);
            setStep(3);

            toast.current?.show({
                severity: 'success',
                summary: 'Éxito',
                detail: `Pedido creado: ${venta.codigoVenta}`,
                life: 5000
            });

            // Redirigir después de 3 segundos
            setTimeout(() => {
                navigate('/tienda');
            }, 3000);
        } catch (err) {
            const errorMsg = err.response?.data?.message || err.message || 'Error al crear la venta';
            setError(errorMsg);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: errorMsg,
                life: 5000
            });
        } finally {
            setLoading(false);
        }
    };

    if (!cliente) {
        return (
            <div className="ec-container" style={{ padding: '2rem 0' }}>
                <Message severity="warn" text="Debes iniciar sesión para continuar con tu compra." style={{ width: '100%' }} />
                <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
                    <Button label="Ir a iniciar sesión" onClick={() => navigate('/tienda/auth?mode=login')} />
                    <Button label="Volver al catálogo" className="p-button-text" onClick={() => navigate('/tienda/catalogo')} />
                </div>
            </div>
        );
    }

    if (cartItems.length === 0) {
        return (
            <div className="ec-container" style={{ padding: '2rem 0' }}>
                <Message severity="info" text="Tu carrito está vacío. Agrega productos para continuar." style={{ width: '100%' }} />
                <div style={{ marginTop: 16 }}>
                    <Button label="Explorar productos" onClick={() => navigate('/tienda/catalogo')} />
                </div>
            </div>
        );
    }

    // Step 3: Confirmation
    if (step === 3) {
        return (
            <div className="ec-container" style={{ padding: '2rem 0', textAlign: 'center' }}>
                <i className="pi pi-check-circle" style={{ fontSize: '3rem', color: 'var(--ec-success)', marginBottom: 16, display: 'block' }}></i>
                <h2>¡Pedido creado exitosamente!</h2>
                <p style={{ color: 'var(--ec-text-secondary)', marginBottom: 24 }}>
                    Tu pedido ha sido procesado. Redirigiendo a la página principal...
                </p>
            </div>
        );
    }

    // Step 2: Payment
    if (step === 2 && cotizacionData) {
        return (
            <div className="ec-container" style={{ padding: '2rem 0', maxWidth: '600px', margin: '0 auto' }}>
                <h2 style={{ marginBottom: 24 }}>Seleccionar método de pago</h2>

                {error && (
                    <Message severity="error" text={error} style={{ width: '100%', marginBottom: 16 }} />
                )}

                <div style={{ background: '#fff', border: '1px solid var(--ec-border)', borderRadius: 'var(--ec-radius)', padding: 24 }}>
                    <div style={{ marginBottom: 20 }}>
                        <label style={{ display: 'block', marginBottom: 8, fontWeight: 600 }}>Método de pago</label>
                        <Dropdown
                            value={paymentType}
                            options={[
                                { label: 'Efectivo', value: 'efectivo' },
                                { label: 'Tarjeta de crédito', value: 'tarjeta' },
                                { label: 'Crédito (pagar después)', value: 'credito' },
                                { label: 'Mixto', value: 'mixto' }
                            ]}
                            onChange={(e) => setPaymentType(e.value)}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <Divider />

                    <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
                        <span>Subtotal</span>
                        <strong>${cotizacionData.subTotal.toFixed(2)}</strong>
                    </div>
                    <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between' }}>
                        <span>Impuesto ({cotizacionData.impuestoPorcentaje}%)</span>
                        <strong>${cotizacionData.impuesto.toFixed(2)}</strong>
                    </div>
                    <Divider />
                    <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', fontSize: '1.1rem' }}>
                        <span>Total</span>
                        <strong style={{ color: 'var(--ec-primary)' }}>${cotizacionData.total.toFixed(2)} {cotizacionData.monedaSimbolo}</strong>
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                            label={loading ? 'Procesando...' : 'Confirmar pago'}
                            icon="pi pi-check"
                            onClick={handleCreateOrder}
                            disabled={loading}
                            style={{ flex: 1 }}
                        />
                        <Button
                            label="Atrás"
                            className="p-button-text"
                            onClick={() => { setStep(1); setCotizacionData(null); }}
                            disabled={loading}
                        />
                    </div>
                </div>

                {loading && <ProgressSpinner style={{ marginTop: 24 }} />}
            </div>
        );
    }

    // Step 1: Review
    return (
        <div className="ec-container" style={{ padding: '2rem 0', maxWidth: '600px', margin: '0 auto' }}>
            <Toast ref={toast} />
            <h2 style={{ marginBottom: 8 }}>Resumen del pedido</h2>

            {hasOutOfStockItems && (
                <Message
                    severity="error"
                    text="Hay productos en el carrito que superan el stock disponible. Ajusta cantidades antes de continuar."
                    style={{ width: '100%', marginBottom: 16 }}
                />
            )}

            {error && (
                <Message severity="error" text={error} style={{ width: '100%', marginBottom: 16 }} />
            )}

            <div style={{ background: '#fff', border: '1px solid var(--ec-border)', borderRadius: 'var(--ec-radius)', padding: 16, marginBottom: 24 }}>
                <h4 style={{ marginBottom: 12 }}>Cliente: {cliente?.nombre || cliente?.email}</h4>
                {cartItems.map((item) => (
                    <div key={item.id} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--ec-border)' }}>
                        <div>
                            <div style={{ fontWeight: 600 }}>{item.name || item.nombre}</div>
                            <div style={{ fontSize: '0.9rem', color: 'var(--ec-text-secondary)' }}>
                                Cantidad: {item.quantity} | Precio: ${(item.price || item.precioVenta).toFixed(2)}
                            </div>
                        </div>
                        <div style={{ fontWeight: 700 }}>${((item.price || item.precioVenta) * item.quantity).toFixed(2)}</div>
                    </div>
                ))}

                <Divider />

                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span>Items</span>
                    <strong>{totalItems}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span>Total estimado</span>
                    <strong style={{ color: 'var(--ec-primary)', fontSize: '1.2rem' }}>${getTotal().toFixed(2)}</strong>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button
                    label="Continuar a pago"
                    icon="pi pi-arrow-right"
                    iconPos="right"
                    onClick={handleGenerateCotizacion}
                    disabled={hasOutOfStockItems || loading}
                    style={{ flex: 1 }}
                />
                <Button
                    label="Volver al carrito"
                    className="p-button-text"
                    onClick={() => navigate('/tienda/catalogo')}
                    disabled={loading}
                />
            </div>

            {loading && <ProgressSpinner style={{ marginTop: 24 }} />}
        </div>
    );
};

export default Checkout;
